import { useState } from "react";
import { StepIndicator } from "./step-indicator";
import { Dialog, DialogContent , DialogTitle} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuthDialog } from "@/context/auth-dialog.context";
import { useIsMobile } from "@/hooks/use-mobile";
import StickermaniaBg1 from "@/assets/svg/stikermania-bg-1.svg";
import StickermaniaBg2 from "@/assets/svg/stikermania-bg-2.svg";
import StickermaniaBg3 from "@/assets/svg/stikermania-bg-3.svg";
import StickermaniaImg1 from "@/assets/svg/stikermania-img-1.png";
import StickermaniaImg2 from "@/assets/svg/stikermania-img-2.png";
import StickermaniaImg3 from "@/assets/svg/stikermania-img-3.png";

interface StepData {
  title: string;
  description: string;
  buttonLabel: string;
  backgroundVariant: "welcome" | "info" | "action";
}

const stepsData: StepData[] = [
  {
    title: "Привет! <br>Вы со Стикермании?",
    description:
      "Добро пожаловать на хоум-игры от Квиз, плиз! Мы создаём интересный досуг для всех-всех-всех.",
    buttonLabel: "Что это такое?",
    backgroundVariant: "welcome",
  },
  {
    title: "Что такое хоум-игры?",
    description:
      "Это интеллектуально-увлекательные игры на всевозможные темы. Вы можете играть в хоумы как хотите, когда хотите, с кем хотите — дома с друзьями, на даче с бабушкой, по пути с работы одному, с дальними родственниками на застолье, на созвоне с коллегами, или против своего кота на диване.",
    buttonLabel: "Go играть!",
    backgroundVariant: "info",
  },
  {
    title: "Go играть!",
    description:
      "Здорово провести время и найти новые интересы – чтобы жизнь стала ещё ярче!",
    buttonLabel: "Зарегистрироваться",
    backgroundVariant: "action",
  },
];

type PopupStep = "welcome" | "info" | "action";

type BackgroundImages = {
  [key in PopupStep]: string
}

const backgroundImages: BackgroundImages = {
  welcome: StickermaniaBg1,
  info: StickermaniaBg2,
  action: StickermaniaBg3,
}

export function IntroDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const isLastStep = currentStep === stepsData.length - 1;

  const isMobile = useIsMobile();

  const { openDialog } = useAuthDialog();

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      openDialog();
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="user-layout max-w-[928px] h-[602px] flex items-center justify-center p-0 !bg-[#450073] overflow-hidden" 
        style={{
          background: "radial-gradient(ellipse at right bottom, rgba(247,0,132,0.6) 0%, rgba(0,212,255,0) 60%)",
        }}
        aria-describedby=""
      >
        <DialogTitle> </DialogTitle>
        <div
          className="w-full h-full bg-bottom px-3 md:px-10 pt-10 pb-6"
          style={{
            backgroundImage: `url(${backgroundImages[stepsData[currentStep].backgroundVariant as keyof BackgroundImages]})`,
          }}
        >
          <div className="flex flex-col justify-between h-full">
            <div className="flex flex-col md:block h-full md:h-auto justify-between">
              <div>
                <h2 dangerouslySetInnerHTML={{ __html: stepsData[currentStep].title }}/>
                <p className="md:text-xl md:font-semibold mb-6 max-w-[500px]">
                  {stepsData[currentStep].description}
                </p>                
              </div>
              <Button
                onClick={handleNext}
                className="w-full md:w-auto"
              >
                {stepsData[currentStep].buttonLabel}
              </Button>                
            </div>

            <div className="gap-2 hidden md:flex">
              {
                <StepIndicator
                  totalSteps={stepsData.length}
                  currentStep={currentStep}
                />
              }
            </div>         
          </div>
        </div>
        <Background variant={stepsData[currentStep].backgroundVariant} />  
      </DialogContent>
    </Dialog>
  );
}

interface BackgroundProps {
  variant: PopupStep;
}

const Background: React.FC<BackgroundProps> = ({ variant }) => {
  switch (variant) {
    case "welcome":
      return (
        <img 
          src={StickermaniaImg1}
          className="absolute -translate-x-1/2 md:translate-x-1/3 left-1/2 md:left-auto max-w-[370px] md:max-w-none -bottom-[205px] md:bottom-auto"
          style={{zIndex: -1}}
        />
      );
    case "info":
      return (
        <img
          src={StickermaniaImg2}
          className="absolute max-w-[300px] md:max-w-none -bottom-4 md:bottom-0 md:right-0"
          style={{zIndex: -1}}
        />
      );
    case "action":
      return (
        <img
          src={StickermaniaImg3}
          className="absolute max-w-[450px] md:max-w-none bottom-2 md:-bottom-[120px] md:-right-[130px] translate-x-[10%] "
          style={{zIndex: -1}}
        />
      );
    default:
      return null;
  }
};
